import { set as _set, isEmpty as _isEmpty } from 'lodash';
import { ContinueType, Node, NodeTypes, SurveyEvents, TextContentHighlightMod, TextContentType } from '@myriadgenetics/mgh-types';
import validator from 'validator';
import { pushGTMData, GTMDataProps } from '../../helpers/googleTagManager';
import { remapAnswer } from '../../helpers/answers';
import { WrappedUseHistory } from '../../helpers/routing';
import { LocationState, MghOrderData, SurveyContainerStep } from './types';
import API from '../../api';
import { ACCESSION_ID_SEARCH_PARAM, AUTH_SLUG, SURVEY_SESSION_START_ORIGIN, queryParamsToInitialize } from './constants';
import { SURVEY_ACCOUNT_ID_SEARCH_PARAM } from '../../helpers/surveyAccount';
import Logger from '../../helpers/logger';
import { TFunction } from 'i18next';
import type { AxiosResponse } from 'axios';
import { getPatientIdentifierKeys, cleanUpSecondaryPatientIdentifier } from './../../helpers/url/index';
import axios from '../../api/axios';
import { ENDPOINT_URLS } from '../../helpers/api';

interface responseUpdate {
  answerKey: string;
  answer: any;
  gtmData?: Partial<GTMDataProps>;
  responses: { [key: string]: any };
  aT404PUrl: boolean;
  history: WrappedUseHistory;
  location: LocationState;
  replaceState?: boolean;
}

interface initSurvey {
  urlSlug: string;
  history: WrappedUseHistory;
  location: LocationState;
  session: string;
  title: string;
  mghOrderId?: string | null;
  mghOrderData?: MghOrderData | null;
}

interface InitData {
  ajs_anonymous_id: string;
  personalBreastCancerHistory: boolean;
  [key: string]: unknown;
}

interface goToNextNodeParams {
  id: number | null | undefined;
  replace?: boolean;
  curNode?: Node | null | undefined;
  survey?: any;
  goForwardOnContinue: boolean;
  history: WrappedUseHistory;
  urlSlug: string;
  location: LocationState;
  t: TFunction;
}

export const updateResponses = ({ answerKey, answer, gtmData, responses, aT404PUrl, history, location, replaceState = true }: responseUpdate): any => {
  // here's where we maintain backwards compatibility
  const newMapping = remapAnswer(answerKey, answer);
  if (Array.isArray(newMapping)) {
    for (const newlyMappedAnswer of newMapping) {
      _set(responses, newlyMappedAnswer.key, newlyMappedAnswer.value);
    }
  }

  // this custom event is only for chatbot...may need to filter out the emit
  const event = new CustomEvent(SurveyEvents.onResponseUpdated, { detail: { answerKey, answer } });
  document.dispatchEvent(event);

  // updated in place so downstream components have updated value right away
  _set(responses, answerKey, answer);

  aT404PUrl && pushGTMData({ answerKey, answer, data: gtmData });
  // be careful not to create new responses object here as pointers will get screwy otherwise
  // You really shouldn't use the responses on the same path without updating state,
  // but skipping replace and using responses for next state is okay.
  // Really, we should stop mutating responses object
  if (replaceState === true) {
    history.replace(location.pathname, {
      ...location.state,
      locResponses: responses,
    });
  }

  // here we will add to the dataLayer
  Logger.logMedalliaData(answerKey, answer);

  return responses;
};

export const getSurveyByIdPath = (id: number): string => `/patient-surveys/${id}`;
export const getSurveyById = async (id: number) => API.get(getSurveyByIdPath(id));

const getPatientIdentifierQueryParams = (location: LocationState): { [key: string]: string } => {
  const patientIdentifierKeys = getPatientIdentifierKeys();
  const search = new URLSearchParams(location.search);
  return patientIdentifierKeys.reduce((acc, key) => {
    if (!key) return acc;
    const patientIdentifierQueryParam = search.get(key);
    if (patientIdentifierQueryParam) {
      const cleanedPatientIdentifier = cleanUpSecondaryPatientIdentifier(patientIdentifierQueryParam);
      acc = { ...acc, [key]: cleanedPatientIdentifier };
    }
    return acc;
  }, {});
};

export const generateAnonymousId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch (e) {
      console.warn('crypto.randomUUID failed:', e);
    }
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const startSurveyEntryTimeLog = async () => {
  try {
    const { data }: AxiosResponse<string> = await axios.get(ENDPOINT_URLS.startSurveyEntryLog);
    return data;
  } catch (error) {
    Logger.logError(error);
    return null;
  }
};

export const initializeSurvey = ({ urlSlug, history, location, session, title, mghOrderData }: initSurvey) => {
  const initData: InitData = {
    ajs_anonymous_id: generateAnonymousId(),
    personalBreastCancerHistory: false,
  };

  const urlContainsAuth = /^\/auth\/?$/.test(location.pathname);
  const sessionStartedOnAuth = window.sessionStorage.getItem(SURVEY_SESSION_START_ORIGIN) === `/${AUTH_SLUG}`;

  const shouldUseAuthFlow = (urlContainsAuth || sessionStartedOnAuth) && !mghOrderData;

  window.analytics?.ready?.(() => {
    window.analytics.reset();
    window.analytics.setAnonymousId(initData.ajs_anonymous_id);
  });

  // Add any query params to our global init data
  const search = new URLSearchParams(location.search);
  queryParamsToInitialize.forEach((qp) => {
    if (search.get(qp)) initData[qp] = search.get(qp);
  });

  const patientIdentifiersQueryParams = getPatientIdentifierQueryParams(location);
  const locResponses = mghOrderData
    ? {
        mghOrderUuid: mghOrderData.uuid,
        mghOrderData,
      }
    : undefined;

  let historyObj: any = {
    title,
    surveyStep: shouldUseAuthFlow ? SurveyContainerStep.Auth : SurveyContainerStep.Lang,
    mghUrl: urlSlug,
    accessionId: search.get(ACCESSION_ID_SEARCH_PARAM),
    surveyEntryAccountId: search.get(SURVEY_ACCOUNT_ID_SEARCH_PARAM),
    locSession: session,
    initData,
    locResponses,
  };

  if (!_isEmpty(patientIdentifiersQueryParams)) {
    historyObj = {
      ...historyObj,
      queryParams: {
        ...patientIdentifiersQueryParams,
      },
    };
  }
  if (shouldUseAuthFlow) {
    history.push(`/${AUTH_SLUG}`, historyObj);
  } else {
    history.push(`/${urlSlug}/lang${location.search || ''}`, historyObj);
  }
};

export const isValidUUID = (uuid: string): boolean => {
  return validator.isUUID(uuid);
};

export const getTagForNode = (node: Node, nodes: any): any | null => {
  switch (node.type) {
    case NodeTypes.ColonPolyp:
      return nodes.ColonPolypNodeReact;
    case NodeTypes.BloodTransfusion:
      return nodes.BloodTransfusionNodeReact;
    case NodeTypes.BreastCancerRisk:
      return nodes.BreastCancerRiskNodeReact;
    case NodeTypes.CancerHistory:
      return nodes.CancerHistoryNodeReact;
    case NodeTypes.ContextBreak:
      return nodes.ContextBreakNodeReact;
    case NodeTypes.EndpointCall:
      return nodes.EndpointCallNode;
    case NodeTypes.Page:
      return nodes.PageNodeReact;
    case NodeTypes.Redirect:
      return nodes.RedirectNodeReact;
    case NodeTypes.Content:
      return nodes.ContentNodeReact;
    case NodeTypes.Chat:
      return nodes.ChatbotWorkflow;
    case NodeTypes.ChatModalNode:
      return nodes.ChatModalNode;
    default:
      return null;
  }
};

const getLangPathRegex = (urlSlug: string) => new RegExp(`[#/](?:${urlSlug}|lang)([/]|/lang)?$`);
export const getIsLangPath = (pathname: string, urlSlug: string) => getLangPathRegex(urlSlug).test(pathname);

export const startState = (history: WrappedUseHistory) => ({
  lengthAtPush: history.length,
  timing: {
    firstPage: new Date().toISOString(),
  },
});

export const isForceStartOver = (atResults: boolean, surveyStep?: SurveyContainerStep) => atResults && surveyStep !== SurveyContainerStep.Results;

export const attachOnBeforeUnloadEvent = (shouldDisplayNotice: boolean | null) => {
  if (shouldDisplayNotice) {
    window.onbeforeunload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = false;
    };
  } else {
    window.onbeforeunload = null;
  }
};

export const findNodeByIdFromSurvey = (id: number | null, survey: any): Node | null | undefined => survey && survey.nodes.find((n: Node) => n.id === id);

export const gotoNextNode = ({ id, replace, curNode, survey, goForwardOnContinue, history, location, urlSlug, t }: goToNextNodeParams) => {
  // TODO figure out how we can make it so CANCER_HISTORY doesn't have to be explicitly ignored
  if (goForwardOnContinue && curNode && curNode.type !== NodeTypes.CancerHistory) {
    history.go(1);
  } else {
    const routeMethod = replace ? history.replace : history.push;
    if (id) {
      const nextNode = findNodeByIdFromSurvey(id, survey);
      if (!nextNode) {
        Logger.logError(`node: ${id} does not exist`);
      } else {
        routeMethod(`/${urlSlug}/survey/node/${id}`, {
          ...location.state,
          title: t(nextNode.name || `myGeneHistory - ${id}`),
          nodeId: id,
        });
      }
    } else {
      routeMethod(`/${urlSlug}/final`, {
        ...location.state,
        title: t('pageTitles.final'),
        surveyStep: SurveyContainerStep.Final,
        nodeId: undefined,
      });
    }
  }
};

export const getLastQuestion = () => {
  return {
    id: 39680,
    type: NodeTypes.Page,
    name: 'Last Question',
    description: 'Thank you screen',
    data: {
      continueKey: 'submitBtn',
      continueType: ContinueType.End,
    },
    components: [
      {
        id: -10,
        type: 'TEXT_CONTENT',
        name: 'Thank you for completing this questionnaire',
        description: 'Thank you for completing this questionnaire',
        data: {
          contentKey: 'thankYouForCompleting',
          highlightModifier: TextContentHighlightMod.Centered,
          type: TextContentType.Text1,
        },
      },
      {
        id: -11,
        type: 'TEXT_CONTENT',
        name: 'Thank you next steps',
        description: 'Thank you for completing this questionnaire',
        data: {
          contentKey: 'thankYouNextSteps',
          highlightModifier: TextContentHighlightMod.Centered,
          type: TextContentType.Text1,
        },
      },
    ],
    flows: [],
  };
};
